import { useCallback, useEffect, useState } from "react";

import { getPreloadedOnboardingImage, preloadSingleImage } from "./onboardingImagePreload";

type OnboardingIllustrationProps = {
  candidates: string[];
  alt: string;
  intrinsicWidth?: number;
  intrinsicHeight?: number;
};

export function OnboardingIllustration({
  candidates,
  alt,
  intrinsicWidth,
  intrinsicHeight,
}: OnboardingIllustrationProps) {
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [failed, setFailed] = useState(false);
  const src: string | undefined = candidates[candidateIndex];
  const [decodedSrc, setDecodedSrc] = useState<string | null>(() =>
    src && getPreloadedOnboardingImage(src) ? src : null,
  );

  const handleError = useCallback(() => {
    setCandidateIndex((current) => {
      const next = current + 1;
      if (next < candidates.length) {
        return next;
      }
      setFailed(true);
      return current;
    });
  }, [candidates.length]);

  useEffect(() => {
    if (!src || getPreloadedOnboardingImage(src)) {
      if (src) setDecodedSrc(src);
      return;
    }

    let cancelled = false;

    preloadSingleImage(src).then((img) => {
      if (cancelled) return;
      if (img) {
        setDecodedSrc(src);
      } else {
        handleError();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [src, handleError]);

  if (failed || candidates.length === 0 || !src) {
    return null;
  }

  const isDecoded = decodedSrc === src;

  if (!isDecoded) {
    return (
      <div
        aria-hidden
        className="relative z-20 mx-auto block max-w-full animate-pulse rounded-2xl bg-white/5"
        style={{
          width: intrinsicWidth,
          aspectRatio:
            intrinsicWidth && intrinsicHeight
              ? `${intrinsicWidth} / ${intrinsicHeight}`
              : undefined,
        }}
      />
    );
  }

  return (
    <img
      key={src}
      src={src}
      alt={alt}
      width={intrinsicWidth}
      height={intrinsicHeight}
      className="relative z-20 mx-auto block h-auto w-auto max-w-full"
      onError={handleError}
    />
  );
}
